/**
 * Canonical did:plc syntax, per the DID PLC spec v0.1 "Identifier Syntax"
 * (https://web.plc.directory/spec/v0.1/did-plc): the identifier part is
 * exactly 24 characters of lowercase, unpadded RFC 4648 base32 — the
 * alphabet a-z and 2-7 (digits 0, 1, 8, 9 never appear).
 *
 * One of four synchronized copies of this rule in the monorepo. Mirror of
 * the plc half of `VALID_DID_RE` in `xrpc-server/src/validation.ts`, of
 * `XRPC_VALID_DID_RE` in `webapp/src/lib/apBridgedIdentity.ts`, and of
 * `PLC_DID_RE` in `mobile/react-native/lib/did.ts`. Keep all four in sync —
 * xrpc-server and mobile are outside the npm workspaces and cannot import
 * this package.
 */
export const PLC_DID_RE = /^did:plc:[a-z2-7]{24}$/

/**
 * Builds the plc.directory DID-document URL for a did:plc identifier,
 * rejecting anything that isn't canonical so nothing but [a-z2-7] ever
 * reaches the URL. Returns the spec's canonical resolver URL
 * (`https://plc.directory/:did`, colons literal — no percent-encoding).
 * Same contract as `buildPlcDidDocUrl` in mobile `lib/did.ts` and in
 * `xrpc-server/src/validation.ts`. Exported for direct unit testing.
 */
export function buildPlcDidDocUrl(did: string): string {
    if (!did.startsWith('did:plc:')) {
        throw new Error(`Not a did:plc identifier: ${did}`)
    }
    if (!PLC_DID_RE.test(did)) {
        throw new Error(`Invalid did:plc identifier: ${did}`)
    }
    return `https://plc.directory/${did}`
}

/**
 * DNS-label syntax (RFC 1035) for a did:web authority: dot-separated labels
 * of [a-zA-Z0-9-], each 1-63 chars, no leading/trailing hyphen, at least two
 * labels. Looser than `DID_WEB_AUTHORITY_RE` in xrpc-server/src/validation.ts
 * (atproto's handle grammar, which also forbids a numeric-leading final
 * label and checks a disallowed-TLD list) because `isValidMentionDid` only
 * ever decides whether to render a profile link — it never resolves or
 * fetches the DID, so that validator's SSRF-flavored checks don't apply here.
 */
const DID_WEB_HOSTNAME_RE =
    /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/

const MAX_DID_WEB_AUTHORITY_LENGTH = 253

/**
 * Whether a richtext mention facet's `did` is safe to render as a profile
 * link: a canonical `did:plc` (`PLC_DID_RE`) or a `did:web` whose authority
 * is a syntactically valid hostname. Anything else — malformed DIDs,
 * injection attempts (`did:plc:abc/../x`, path/query/fragment characters),
 * or a missing `did` — is unsafe, and the mention must render as plain text
 * with no link. Mirrors the semantics of `isValidMentionDid` in
 * mobile/react-native/lib/articleHelpers.ts on branch
 * feat/article-content-format-registry (PR #2897, not yet merged to main
 * as of this writing — mobile has no Offprint/pckt mention surface on main).
 */
export function isValidMentionDid(did: unknown): did is string {
    if (typeof did !== 'string') return false
    if (PLC_DID_RE.test(did)) return true
    if (!did.startsWith('did:web:')) return false
    const authority = did.slice('did:web:'.length)
    return (
        authority.length > 0 &&
        authority.length <= MAX_DID_WEB_AUTHORITY_LENGTH &&
        DID_WEB_HOSTNAME_RE.test(authority)
    )
}
